import * as React from 'react'
import { injectIntl, type WrappedComponentProps } from 'react-intl'
import type moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import Caption from 'LuxKit/Typography/Caption'
import CalendarGrid from './CalendarGrid'
import memoize from 'lib/memoize/memoize'
import type { SearchCalendarEvent } from 'constants/searchCalendarEvents'
import { getCalendarWeekStart, orderCalendarWeekdays } from 'lib/datetime/calendarUtils'

const CalendarContainer = styled.div`
  > * + * {
    margin-top: ${rem(4)};
  }
`

const WeekHeader = styled(Caption)`
  display: grid;
  grid-template-columns: repeat(7 , 1fr);
`

const RelativeBlock = styled.div`
  position: relative;
`

interface Props {
  className?: string
  // month and year this calendar is for
  month: number
  year: number
  // minimum and maximum dates that are selectable
  minDate?: Date
  maxDate?: Date
  // the selected dates
  startDate?: Date
  endDate?: Date
  // Labels that'll appear in the start/end days
  startLabel?: string
  endLabel?: string
  overlayEvents?: ReadonlyArray<Pick<SearchCalendarEvent, 'id' | 'startDate' | 'endDate' | 'color' | 'icon'>>
  overlayHolidays?: ReadonlyArray<Pick<SearchCalendarEvent, 'id' | 'startDate' | 'endDate' | 'title'>>
  onDayClick: (day: moment.Moment) => void
}
const getStartOfMonth = memoize((month: number, year: number) => new Date(year, month), (month, year) => `${month}${year}`)

// Class used to prevent onDayClick reference from changing on each click
class Calendar extends React.PureComponent<React.PropsWithChildren<Props & WrappedComponentProps>> {
  onDayClick = (day: moment.Moment) => {
    this.props.onDayClick(day)
  }

  render() {
    const {
      intl,
      className,
      startDate,
      endDate,
      minDate,
      maxDate,
      startLabel,
      endLabel,
      overlayEvents,
      overlayHolidays,
      month,
      year,
    } = this.props

    const startOfMonth = getStartOfMonth(month, year)

    const sundayFull = intl.formatMessage({
      id: '9iivdXkUw2drku628wso',
      defaultMessage: 'Sunday',
    })
    const mondayFull = intl.formatMessage({
      id: 'm49b3Rl6gdOPh2cG9zxC',
      defaultMessage: 'Monday',
    })
    const tuesdayFull = intl.formatMessage({
      id: 'U0kB7ll2jMxjcJbu3t4U',
      defaultMessage: 'Tuesday',
    })
    const wednesdayFull = intl.formatMessage({
      id: 'bs_4M2IEMrmmV4Nne9WA',
      defaultMessage: 'Wednesday',
    })
    const thursdayFull = intl.formatMessage({
      id: 'j7lDWi8MC7YyyzTnxwSv',
      defaultMessage: 'Thursday',
    })
    const fridayFull = intl.formatMessage({
      id: 'LAzZnDpOOjRLllGZNrGi',
      defaultMessage: 'Friday',
    })
    const saturdayFull = intl.formatMessage({
      id: 'pbzjKOhWMNa_ny5HkMaa',
      defaultMessage: 'Saturday',
    })

    const sundayAbbr = intl.formatMessage({
      id: 'En8ySE2GjAvLOBZZfNXp',
      defaultMessage: 'Sun',
    })
    const mondayAbbr = intl.formatMessage({
      id: 'atbMBEtMVvHT2DCo6PUr',
      defaultMessage: 'Mon',
    })
    const tuesdayAbbr = intl.formatMessage({
      id: 'TcEu6j5gejUWCoznQ41K',
      defaultMessage: 'Tue',
    })
    const wednesdayAbbr = intl.formatMessage({
      id: 'QDw3l7mWrdEZRB8xmJun',
      defaultMessage: 'Wed',
    })
    const thursdayAbbr = intl.formatMessage({
      id: 'nc5kku8lIZht9AH3Zs8d',
      defaultMessage: 'Thu',
    })
    const fridayAbbr = intl.formatMessage({
      id: 'X7hmrIKWsYX87SpNUC6p',
      defaultMessage: 'Fri',
    })
    const saturdayAbbr = intl.formatMessage({
      id: 'Z9I6hXp_rGt2xqGhggYP',
      defaultMessage: 'Sat',
    })

    return (
      <CalendarContainer className={className}>
        <WeekHeader variant="medium" align="start" colour="light-foreground-alt">
          {orderCalendarWeekdays([
            [sundayFull, sundayAbbr], [mondayFull, mondayAbbr], [tuesdayFull, tuesdayAbbr], [wednesdayFull, wednesdayAbbr], [thursdayFull, thursdayAbbr], [fridayFull, fridayAbbr], [saturdayFull, saturdayAbbr],
          ], getCalendarWeekStart(intl.locale)).map(([full, abbr]) => <abbr key={full} title={full}>{abbr}</abbr>)}
        </WeekHeader>
        <RelativeBlock>
          <CalendarGrid
            minDate={minDate}
            maxDate={maxDate}
            startOfMonth={startOfMonth}
            onDayClick={this.onDayClick}
            startDate={startDate}
            endDate={endDate}
            startLabel={startLabel}
            endLabel={endLabel}
            overlayEvents={overlayEvents}
            overlayHolidays={overlayHolidays}
            weekStart={getCalendarWeekStart(intl.locale)}
          />
        </RelativeBlock>
      </CalendarContainer>
    )
  }
}

export default injectIntl(Calendar)
